<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div
        v-if="state.type"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="state.title ? 'app-dialog-title' : undefined"
        aria-describedby="app-dialog-desc"
      >
        <div
          class="absolute inset-0 bg-black/50 transition-opacity"
          aria-hidden="true"
          @click="state.type === 'alert' ? close(true) : close(false)"
        />
        <div
          ref="dialogPanelRef"
          class="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          @click.stop
        >
          <div
            :class="[
              'px-6 pt-5 pb-1',
              variantClasses[state.variant]?.header ?? 'bg-gray-50 dark:bg-gray-700/50',
            ]"
          >
            <h2
              id="app-dialog-title"
              class="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {{ state.title }}
            </h2>
          </div>
          <div id="app-dialog-desc" class="px-6 py-4">
            <p class="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{{ state.message }}</p>
          </div>
          <div class="px-6 pb-6 pt-2 flex gap-3 justify-end">
            <template v-if="state.type === 'alert'">
              <button
                type="button"
                class="btn btn-primary"
                @click="close(true)"
              >
                OK
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                class="btn btn-secondary"
                @click="close(false)"
              >
                {{ state.cancelLabel ?? 'Cancel' }}
              </button>
              <button
                type="button"
                :class="state.variant === 'error' ? 'btn btn-danger' : 'btn btn-primary'"
                @click="close(true)"
              >
                {{ state.confirmLabel ?? 'OK' }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useAppDialog } from '@/composables/useAppDialog';

const { state, close } = useAppDialog();
const dialogPanelRef = ref<HTMLElement | null>(null);

const variantClasses: Record<string, { header: string }> = {
  success: { header: 'bg-green-50 dark:bg-green-900/30' },
  warning: { header: 'bg-amber-50 dark:bg-amber-900/30' },
  error: { header: 'bg-red-50 dark:bg-red-900/30' },
  info: { header: 'bg-gray-50 dark:bg-gray-700/50' },
};

// Scroll lock and focus when dialog opens
let savedOverflow = '';
watch(
  () => state.type,
  (type) => {
    if (type) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const btn = dialogPanelRef.value?.querySelector<HTMLElement>('button');
          if (btn) btn.focus();
        });
      });
    } else {
      document.body.style.overflow = savedOverflow;
    }
  }
);
</script>

<style scoped>
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.2s ease;
}
.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}
.dialog-enter-active .relative,
.dialog-leave-active .relative {
  transition: transform 0.2s ease;
}
.dialog-enter-from .relative,
.dialog-leave-to .relative {
  transform: scale(0.98);
}
</style>
